<template>
  <div class="t3m-ObjectiveFinder-comparison">
    <div
      class="overflow-x-auto rounded shadow ring-1 ring-black ring-opacity-10 md:rounded-lg"
    >
      <table
        v-if="objectivesToCompare"
        class="min-w-4xl text-left divide-y divide-gray-300 t3m-ObjectiveFinder-comparison-table"
      >
        <colgroup>
          <col class="w-[1%]" />
          <col
            v-for="(o, index) in objectivesToCompare"
            :key="index"
            class="w-[1%]"
            :style="`background-color: ${chartColorPalette[index]}14;`"
          />
        </colgroup>
        <thead class="t3m-bg-gray-100 !border-t-0">
          <tr>
            <th class="py-3.5 pl-4 pr-3 text-left sm:pl-6 align-bottom">
              <h4 class="mb-0 text-sm font-normal text-t3m-muted">
                {{ translations.Compare }}
              </h4>
            </th>
            <th
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-3.5 pl-4 pr-3 text-left sm:pl-6"
            >
              <h4 class="mb-0">
                {{ o.objectiveName }}
              </h4>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="bg-gray-100 t3m-ObjectiveFinder-comparison-table-image">
            <th class="py-4 pl-4 pr-3 sm:pl-6 border-r border-gray-300"></th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
              :class="{
                'border-r border-gray-300':
                  index < objectivesToCompare.length - 1,
              }"
            >
              <img
                class="w-full mx-auto mt-4 max-w-56 saturate-20 mix-blend-multiply"
                :src="o.picturePath"
                alt
              />
            </td>
          </tr>
          <tr class="bg-gray-100">
            <th class="py-4 pl-4 pr-3 sm:pl-6 border-r border-gray-300"></th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="p-4 sm:p-8 border-b-4 align-bottom"
              :class="{
                'border-r border-gray-300':
                  index < objectivesToCompare.length - 1,
              }"
              :style="`border-bottom-color: ${chartColorPalette[index]};`"
            >
              <ShopPanel v-if="shouldShowShopPanel(o)" :objective="o" />
              <div v-else class="flex items-center justify-center">
                <a data-fancybox
                   data-width="1024"
                   data-type="iframe"
                   rel="nofollow"
                   :href="encodedContactURL(o)"
                   class="t3m-ObjectiveFinder-list-action-contact t3m-FancyboxTrigger t3m-FancyboxTrigger--contact btn btn-sm btn-primary">{{
                  translations.GetQuote
                }}</a>
              </div>
              <div
                class="mt-4 flex items-center"
                :class="{
                  'justify-center': !shouldShowShopPanel(o),
                  'justify-end': shouldShowShopPanel(o),
                }"
              >
                <a
                  :href="o.singleViewPageUrl"
                  target="_blank"
                  class="flex items-center space-x-2 t3m-ObjectiveFinder-details-permaLink text-xs"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    class="w-3 h-3"
                    fill="currentColor"
                  >
                    <path
                      d="M314.222 197.78c51.091 51.091 54.377 132.287 9.75 187.16-6.242 7.73-2.784 3.865-84.94 86.02-54.696 54.696-143.266 54.745-197.99 0-54.711-54.69-54.734-143.255 0-197.99 32.773-32.773 51.835-51.899 63.409-63.457 7.463-7.452 20.331-2.354 20.486 8.192a173.31 173.31 0 0 0 4.746 37.828c.966 4.029-.272 8.269-3.202 11.198L80.632 312.57c-32.755 32.775-32.887 85.892 0 118.8 32.775 32.755 85.892 32.887 118.8 0l75.19-75.2c32.718-32.725 32.777-86.013 0-118.79a83.722 83.722 0 0 0-22.814-16.229c-4.623-2.233-7.182-7.25-6.561-12.346 1.356-11.122 6.296-21.885 14.815-30.405l4.375-4.375c3.625-3.626 9.177-4.594 13.76-2.294 12.999 6.524 25.187 15.211 36.025 26.049zM470.958 41.04c-54.724-54.745-143.294-54.696-197.99 0-82.156 82.156-78.698 78.29-84.94 86.02-44.627 54.873-41.341 136.069 9.75 187.16 10.838 10.838 23.026 19.525 36.025 26.049 4.582 2.3 10.134 1.331 13.76-2.294l4.375-4.375c8.52-8.519 13.459-19.283 14.815-30.405.621-5.096-1.938-10.113-6.561-12.346a83.706 83.706 0 0 1-22.814-16.229c-32.777-32.777-32.718-86.065 0-118.79l75.19-75.2c32.908-32.887 86.025-32.755 118.8 0 32.887 32.908 32.755 86.025 0 118.8l-45.848 45.84c-2.93 2.929-4.168 7.169-3.202 11.198a173.31 173.31 0 0 1 4.746 37.828c.155 10.546 13.023 15.644 20.486 8.192 11.574-11.558 30.636-30.684 63.409-63.457 54.733-54.735 54.71-143.3-.001-197.991z"
                    />
                  </svg>
                  <span>{{ translations.DetailsPage }}</span>
                </a>
              </div>
            </td>
          </tr>
          <tr
            v-if="!this.$root.$browserDetect.isIE"
            class="t3m-ObjectiveFinder-comparison-table-chart"
          >
            <td :colspan="objectivesToCompare.length + 1" class="p-4 sm:p-8">
              <chart :objectives="objectivesToCompare" />
            </td>
          </tr>
          <tr
            v-if="this.$root.$browserDetect.isIE"
            class="t3m-ObjectiveFinder-comparison-table-transmissionCurveImage"
          >
            <th class="py-4 pl-4 pr-3 sm:pl-6"></th>
            <td v-for="(o, index) in objectivesToCompare" :key="index">
              <img :src="o.transmissionCurvePicturePath" alt />
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.NumericalAperture }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.aperture }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.SuitableForAFC }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ canDoLedafValue(o) }}
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.CorrectionRing }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ corrValue(o) }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.Coverglass }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ coverGlassValue(o) }}
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.ExitPupilPositionDICprism }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ exitPupilPosValue(o) }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.FieldOfView }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.fov }}
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">{{ translations.Immersion }}</th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.immersion }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.IrisDiaphragm }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ irisDiaphragmValue(o) }}
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.Magnification }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ magnificationValue(o) }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.FreeWorkingDistance }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.freeWorkingDistance }}
            </td>
          </tr>
          <tr class="bg-gray-900 bg-opacity-5">
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.ObjectiveType }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.objectiveType }}
            </td>
          </tr>
          <tr>
            <th class="py-4 pl-4 pr-3 sm:pl-6">
              {{ translations.ArticleNumber }}
            </th>
            <td
              v-for="(o, index) in objectivesToCompare"
              :key="index"
              class="py-4 pl-4 pr-3 sm:pl-6"
            >
              {{ o.matnrShort }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { valueFormatters } from '../shared'
import { mapState } from 'vuex'
import Chart from '@/components/Chart.vue'
import ShopPanel from '@/components/ShopPanel.vue'

export default {
  name: 'ObjectiveComparison',
  data() {
    return {
      // Default ApexChart palette
      // https://apexcharts.com/docs/options/theme/
      chartColorPalette: [
        '#008FFB',
        '#00E396',
        '#FEB019',
        '#FF4560',
        '#775DD0',
      ],
    }
  },
  components: {
    Chart,
    ShopPanel,
  },
  computed: {
    ...mapState([
      'contrastMethodSets',
      'selectedObjective',
      'objectivesToCompare',
      'translations',
      'shopEnabled',
    ]),
  },
  methods: {
    isShopProduct(objective) {
      return Boolean(
        (objective &&
          objective.availableonline &&
          objective.availableonline.length) ||
          (objective &&
            objective.availabletopurchase &&
            objective.availabletopurchase.length)
      )
    },
    shouldShowShopPanel(objective) {
      return this.shopEnabled && this.isShopProduct(objective)
    },
  },
  mixins: [valueFormatters],
}
</script>
